/* THE REVIEW'S PHOTO ON THE WEB (2026-10-07, `docs/REVIEW-COMPOSER-BRIEF.md` Part A) — a picked image becomes ONE JPEG:
 * the long edge at most `PHOTO_MAX_EDGE`, quality `PHOTO_QUALITY`, drawn through a canvas — which keeps the pixels and
 * DROPS every EXIF field (the GPS position above all). The phone's twin is `ReviewPhoto` (Swift). A format this browser
 * cannot decode (HEIC on Chrome) says so in one line; it never fails silently. */

export const PHOTO_MAX_EDGE = 2048;
export const PHOTO_QUALITY = 0.8;
/** The picker's formats — named, not a wildcard (a slash-star in a source file reads as a comment to the harness). */
export const PHOTO_ACCEPT = "image/jpeg,image/png,image/heic,image/heif,image/webp,image/gif";
export const PHOTO_UNREADABLE = "This browser cannot open that image — choose a JPEG or PNG.";

/** The drawn size: the long edge brought down to `max` (never up), the aspect kept, whole pixels, at least 1. */
export function fitWithin(width: number, height: number, max = PHOTO_MAX_EDGE): { width: number; height: number } {
  if (!(width > 0) || !(height > 0)) return { width: 1, height: 1 };
  const k = Math.min(1, max / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * k)), height: Math.max(1, Math.round(height * k)) };
}

/** The file → the JPEG to upload, or the one line that says why not. */
export async function toReviewJpeg(file: Blob): Promise<Blob | string> {
  let bitmap: ImageBitmap;
  try { bitmap = await createImageBitmap(file, { imageOrientation: "from-image" }); } catch { return PHOTO_UNREADABLE; }
  const { width, height } = fitWithin(bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (ctx === null) { bitmap.close(); return PHOTO_UNREADABLE; }
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", PHOTO_QUALITY));
  return blob ?? PHOTO_UNREADABLE;
}
